import React from 'react';
import snowflakeIcon from 'assets/logos/partners/snowflake-icon.svg';
import { motion, MotionProps } from 'motion/react';

interface SnowflakeIconProps extends Omit<MotionProps, 'src'> {
  /**
   * Optional CSS class name(s) to apply to the icon
   */
  className?: string;
  /**
   * Size of the icon (applies to both width and height)
   * @default 'h-5 w-5'
   */
  size?: string;
  /**
   * Whether the icon should be hidden from screen readers
   * @default true
   */
  ariaHidden?: boolean;
  /**
   * Alt text for the icon (for accessibility when ariaHidden is false)
   */
  alt?: string;
}

/**
 * SnowflakeIcon Component
 *
 * A reusable component that displays the Snowflake logo icon.
 * The icon PNG is served from the public/static directory.
 * Supports all Motion props for animations and interactions.
 *
 * @component
 * @example
 * ```tsx
 * // Default usage
 * <SnowflakeIcon />
 *
 * // Custom size and styling
 * <SnowflakeIcon size="h-8 w-8" className="rounded-lg bg-gray-100 p-1" />
 *
 * // With accessibility
 * <SnowflakeIcon ariaHidden={false} alt="Snowflake account icon" />
 *
 * // With motion props
 * <SnowflakeIcon
 *   initial={{ opacity: 0, scale: 0.5 }}
 *   animate={{ opacity: 1, scale: 1 }}
 *   transition={{ duration: 0.3 }}
 *   whileHover={{ scale: 1.1 }}
 * />
 * ```
 *
 * @param props - Component props (including all HTMLMotionProps)
 * @returns Snowflake icon image element
 */
const SnowflakeIcon: React.FC<SnowflakeIconProps> = ({
  className = '',
  size = 'h-5 w-5',
  ariaHidden = true,
  alt = 'Snowflake',
  ...motionProps
}) => {
  return (
    <motion.img
      src={snowflakeIcon}
      className={`${size} ${className}`.trim()}
      aria-hidden={ariaHidden}
      alt={ariaHidden ? '' : alt}
      {...motionProps}
    />
  );
};

export default SnowflakeIcon;
